import React, { useState, useEffect, useRef } from 'react';
import { generateClient } from '@aws-amplify/api';  // ✅ Use generateClient() instead of API.graphql()
import { MessageCircle, X } from 'lucide-react';

// Initialize Amplify API client
const client = generateClient();

// Subscription for new unread message notifications
const onNewUnreadMessage = /* GraphQL */ `
  subscription OnNewUnreadMessage {
    onNewUnreadMessage {
      submissionId
      conversationId
      unreadCount
      lastMessagePreview
      timestamp
      mobileNumber
    }
  }
`;

// Toast Notification Component
const MessageToast = ({ notification, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Allow time for fade-out animation
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  // Format message time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Redirect to conversation
  const goToConversation = () => {
    window.location.href = `/submissions/${notification.submissionId}/conversation/${notification.conversationId}`;
    onClose();
  };

  return (
    <div
      className={`fixed bottom-4 right-4 bg-white shadow-lg rounded-lg overflow-hidden max-w-md border-l-4 border-blue-500 transform transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-500">
              <MessageCircle size={20} />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-medium text-gray-900">New Message</h3>
              <button
                onClick={onClose}
                className="ml-4 text-gray-400 hover:text-gray-500"
              >
                <X size={16} />
              </button>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              From: <span className="font-medium">{notification.mobileNumber}</span>
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {notification.lastMessagePreview.length > 50
                ? `${notification.lastMessagePreview.substring(0, 50)}...`
                : notification.lastMessagePreview}
            </p>
            <p className="mt-1 text-xs text-gray-400">{formatTime(notification.timestamp)}</p>
            <div className="mt-3 flex justify-end">
              <button
                onClick={goToConversation}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                View Message
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// **Main SMS Notification System Component**
const SMSNotificationSystem = () => {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const subscription = client.graphql({
      query: onNewUnreadMessage,
    }).subscribe({
      next: ({ data }) => {
        if (data && data.onNewUnreadMessage) {
          setNotifications((prev) => [...prev, data.onNewUnreadMessage]);
        }
      },
      error: (error) => console.error("Subscription error:", error),
    });

    return () => subscription.unsubscribe();
  }, []);

  // Remove a notification
  const removeNotification = (index) => {
    setNotifications((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      {notifications.map((notification, index) => (
        <MessageToast
          key={notification.conversationId}
          notification={notification}
          onClose={() => removeNotification(index)}
        />
      ))}
    </>
  );
};

export default SMSNotificationSystem;
